import { z } from "zod";
import { CURRENCY_CODES } from "../money/currency.ts";
import { LOCALE_CODES } from "../i18n/locales.ts";

/**
 * The diner surface (`08-pos-terminal-and-qr.md` § C).
 *
 * Public, unauthenticated, and reached from a printed code — so every schema
 * here is deliberately narrow. § C is explicit: "Never expose staff-only data
 * (costs, other tables, reports) on this surface. It is a public endpoint with
 * a guessable-adjacent URL — treat it as hostile input."
 *
 * That is why this is its own contract file rather than a reuse of the store
 * one: sharing a schema with the back office is how a cost column ends up on
 * a diner's phone one refactor later.
 */

const Money = z.string().regex(/^\d+$/, "minor units, as digits");

/**
 * Where a scanned table code has to land: `/{storeSlug}/t/{qrToken}`, which is
 * the route `apps/web-diner` serves (§ C: "Entry: scan the table QR →
 * `/{storeSlug}/t/{qrToken}`").
 *
 * Here rather than in the back office because two apps have to agree on it and
 * neither can see the other. The back office prints the code; the diner app
 * parses what a phone arrives with; a divergence between them is a hundred
 * table tents that lead to a 404, discovered by a customer.
 *
 * `origin` is the diner deployment's own — a separate host from the back
 * office, so it cannot be derived and has to be configured. Returns null
 * rather than a half-built URL when either half is missing: a code that
 * encodes a broken link is worse than no code, because it looks like it works.
 */
export function dinerTableUrl(
  origin: string | undefined | null,
  storeSlug: string | undefined | null,
  qrToken: string,
): string | null {
  if (!origin || !storeSlug) return null;
  return `${origin.replace(/\/+$/, "")}/${storeSlug}/t/${qrToken}`;
}

/** What scanning a table's code gets you. */
export const DinerSessionSchema = z.object({
  /** Carried on every later call as `X-Diner-Session`. */
  token: z.string(),
  expiresAt: z.string(),
  store: z.object({
    name: z.string(),
    slug: z.string(),
    logoUrl: z.string().nullable(),
    currency: z.enum(CURRENCY_CODES),
    minorUnit: z.number().int(),
    languages: z.array(z.enum(LOCALE_CODES)),
    /** Whether the station is taking orders at all right now. */
    isOpen: z.boolean(),
    /** Settle before the food arrives, or at the end. */
    payFirst: z.boolean(),
    /** Metres a diner may be from the restaurant, or null for no limit. */
    orderingDistanceLimitM: z.number().int().nullable(),
  }),
  table: z
    .object({
      id: z.uuid(),
      name: z.string(),
      capacity: z.number().int(),
    })
    .nullable(),
});

export const DinerMenuItemSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string().nullable(),
  price: Money,
  /** A strikethrough price, when the item is on offer. */
  wasPrice: Money.nullable(),
  picUrl: z.string().nullable(),
  labels: z.array(z.string()),
  isSoldOut: z.boolean(),
  /**
   * How many have sold, or null where the merchant does not show it
   * (`ITEM_MGMT_DISPLAY_SALES`). Null and zero are different answers: nothing
   * sold yet is worth printing, "we do not publish that" is not.
   */
  soldCount: z.number().int().nullable(),
  categoryIds: z.array(z.uuid()),
  specificationGroups: z.array(
    z.object({
      id: z.uuid(),
      name: z.string(),
      minSelect: z.number().int(),
      maxSelect: z.number().int(),
      options: z.array(z.object({ name: z.string(), price: Money })),
    }),
  ),
  toppings: z.array(z.object({ name: z.string(), price: Money })),
  tags: z.array(z.string()),
});

export const DinerMenuSchema = z.object({
  /** The staleness check the client caches against *(verified — `goods/version`)*. */
  version: z.string(),
  categories: z.array(z.object({ id: z.uuid(), name: z.string(), sort: z.number().int() })),
  items: z.array(DinerMenuItemSchema),
  banners: z.array(
    z.object({ id: z.uuid(), imageUrl: z.string(), linkUrl: z.string().nullable() }),
  ),
});
export type DinerMenu = z.infer<typeof DinerMenuSchema>;
export type DinerMenuItem = z.infer<typeof DinerMenuItemSchema>;

/**
 * A chosen option, named and **not** priced.
 *
 * What it costs is looked up from the item's own groups when the order is
 * written, never taken from here — the same rule the base price has always
 * followed on this surface, and the one that makes § C's "treat it as hostile
 * input" true of the money as well as the ids. A phone that could name its own
 * price could have a paid extra for nothing.
 */
const DinerOptionSchema = z.object({ name: z.string().max(80) });

const DinerLineSchema = z.object({
  itemId: z.uuid(),
  quantity: z.string().regex(/^\d+(\.\d{1,3})?$/),
  specifications: z.array(DinerOptionSchema).max(20).default([]),
  toppings: z.array(DinerOptionSchema).max(20).default([]),
  note: z.string().max(240).nullable().default(null),
});

export const PlaceDinerOrderSchema = z.object({
  lines: z.array(DinerLineSchema).min(1).max(100),
  /**
   * The language the menu was read in, the same value `GET /menu` takes.
   *
   * An order line snapshots the names of the item and of every option chosen,
   * and those names are what the kitchen ticket and the receipt print. Without
   * this the diner reads Khmer and the kitchen gets English — and the options
   * named below, which arrive in whatever language they were shown in, would
   * not match the catalogue the server prices them from.
   */
  lang: z.string().max(8).optional(),
  peopleCount: z.number().int().positive().max(99).nullable().default(null),
  /** Checked against the store's geofence when it has one. */
  latitude: z.number().min(-90).max(90).nullable().default(null),
  longitude: z.number().min(-180).max(180).nullable().default(null),
  /** Identifying the diner by phone, as § C describes. */
  phone: z.string().trim().max(32).nullable().default(null),
});

/**
 * What a diner may see of their own order.
 *
 * Deliberately not the store's `OrderSchema`: no cost, no employee, no
 * internal ids beyond the ones this surface needs.
 */
export const DinerOrderSchema = z.object({
  id: z.uuid(),
  orderNo: z.string(),
  pickupCode: z.string().nullable(),
  status: z.enum(["pending", "accepted", "preparing", "served", "settled", "cancelled"]),
  payStatus: z.enum(["unpaid", "partly_paid", "paid", "credit", "refunded", "cancelled"]),
  currency: z.enum(CURRENCY_CODES),
  minorUnit: z.number().int(),
  orderAmount: Money,
  discountAmount: Money,
  serviceCharge: Money,
  taxTotal: Money,
  receivableAmount: Money,
  paidAmount: Money,
  tableName: z.string().nullable(),
  items: z.array(
    z.object({
      name: z.string(),
      quantity: z.string(),
      unitPrice: Money,
      netAmount: Money,
      note: z.string().nullable(),
      status: z.string(),
    }),
  ),
  placedAt: z.string(),
});

export const DinerCouponSchema = z.object({ code: z.string().trim().min(1).max(40) });

// ── The diner's own membership (`08-pos-terminal-and-qr.md` § C) ────────────

/**
 * Everything below is a diner reading *their own* record, which is the one
 * thing on this surface that is not public. § C's rule still governs it:
 * "Never expose staff-only data (costs, other tables, reports) on this
 * surface." A member sees their balance, their points and their coupons — not
 * the store's margin on what they bought, not another member, and not the
 * cashier who served them.
 */

/** Asking for a code. The phone is free-form because the world is. */
export const DinerSigninRequestSchema = z.object({
  phone: z.string().trim().min(3).max(32),
});

export const DinerSigninChallengeSchema = z.object({
  challengeId: z.uuid(),
  expiresAt: z.string(),
  /**
   * The code itself, in development only.
   *
   * There is no SMS provider in this build, so without this the flow cannot be
   * completed at all. Absent in production, where the missing channel becomes
   * a visible dead end rather than a bypass that ships — the same trade
   * `lib/otp.ts` makes for the operator's second factor.
   */
  devCode: z.string().nullable(),
});

export const DinerSigninVerifySchema = z.object({
  challengeId: z.uuid(),
  phone: z.string().trim().min(3).max(32),
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/, "six digits"),
  /**
   * Given when the phone is not yet a member of this chain, which is what
   * turns the sign-in into a registration. Ignored when it already is: a name
   * the merchant has on file is not a diner's to overwrite from a phone.
   */
  name: z.string().trim().min(1).max(120).optional(),
});

/** One stored-value account. A member holds one per currency. */
export const DinerBalanceSchema = z.object({
  currency: z.enum(CURRENCY_CODES),
  minorUnit: z.number().int(),
  balance: Money,
  /** Held against an open bill, and not spendable twice. */
  frozen: Money,
  /** `balance - frozen`, computed here so the phone never does money arithmetic. */
  spendable: Money,
  /**
   * Whether a PIN is set. Not the PIN, and not a way to check one — the till
   * asks for it at settlement. This is here so the app can say "you will be
   * asked for your PIN" rather than surprising somebody at the counter.
   */
  hasPin: z.boolean(),
});

export const DinerMemberSchema = z.object({
  memberNo: z.string(),
  name: z.string().nullable(),
  /** The digits, as stored. Their own number, shown back for confirmation. */
  phone: z.string(),
  tier: z.string().nullable(),
  points: z.string(),
  /**
   * What those points are worth here, in minor units, and what it takes to
   * start spending them. Null where the store does not run points at all —
   * which is a different thing from a balance of zero.
   */
  pointsValue: Money.nullable(),
  minimumRedeemablePoints: z.string().nullable(),
  /**
   * Every currency the member holds in this chain. Usually one. A chain
   * spanning a USD store and a KHR store keeps two, because `12-chain.md` § 8
   * forbids pooling across currencies and converting at top-up would be a
   * liability rather than a convenience.
   */
  balances: z.array(DinerBalanceSchema),
});

/** A signed-in session: the scan's session, plus who it now belongs to. */
export const DinerMemberSessionSchema = z.object({
  token: z.string(),
  expiresAt: z.string(),
  member: DinerMemberSchema,
  /** True when this sign-in created the membership rather than finding it. */
  isNew: z.boolean(),
});

export const DinerBalanceMovementSchema = z.object({
  kind: z.enum(["topup", "spend", "refund", "adjust"]),
  /** Signed: a top-up is positive, a spend is negative. */
  amount: z.string(),
  bonusAmount: Money,
  balanceAfter: Money,
  currency: z.enum(CURRENCY_CODES),
  /** Which branch, since a chain's member moves between them. */
  storeName: z.string().nullable(),
  orderNo: z.string().nullable(),
  occurredAt: z.string(),
});

export const DinerPointsMovementSchema = z.object({
  kind: z.enum(["earn", "redeem", "expire", "adjust"]),
  amount: z.string(),
  balanceAfter: z.string(),
  storeName: z.string().nullable(),
  orderNo: z.string().nullable(),
  occurredAt: z.string(),
});

/** A coupon in the member's own wallet. */
export const DinerWalletCouponSchema = z.object({
  code: z.string(),
  name: z.string(),
  kind: z.enum(["amount", "percent"]),
  /** Minor units for `amount`, basis points for `percent`. */
  value: z.string(),
  minSpend: Money.nullable(),
  maxDiscount: Money.nullable(),
  status: z.enum(["issued", "used", "expired", "void"]),
  expiresAt: z.string().nullable(),
  /** Whether it could be applied to a bill right now, at this store. */
  isUsable: z.boolean(),
});

/**
 * What a top-up would cost and give.
 *
 * Read-only, and it has to be: taking a diner's money needs a payment rail,
 * and v1 ships a mock provider only. Showing the offer without being able to
 * accept it is the honest half — a "top up" button that credited stored value
 * against a payment nothing took would be inventing money.
 */
export const DinerTopupTierSchema = z.object({
  payAmount: Money,
  bonusAmount: Money,
  /** `payAmount + bonusAmount`, so the phone does no money arithmetic. */
  creditedAmount: Money,
  currency: z.enum(CURRENCY_CODES),
  minorUnit: z.number().int(),
});
